import type { DocMode } from '@blocksuite/affine/model';
import type { DocMeta } from '@blocksuite/affine/store';
import {
  Store,
  yjsGetPath,
  yjsObserve,
  yjsObserveDeep,
  yjsObservePath,
} from '@toeverything/infra';
import { nanoid } from 'nanoid';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  of,
  ReplaySubject,
  share,
  switchMap,
} from 'rxjs';
import { Array as YArray, Map as YMap, transact } from 'yjs';

import type { WorkspaceService } from '../../workspace';
import {
  type DocMetaEntry,
  firstDocMetaEntries,
  groupDocMetaEntries,
  isTrashedEntry,
  mergeDocMetaEntries,
} from './doc-meta-entries';
import type { DocPropertiesStore } from './doc-properties';

export class DocsStore extends Store {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly docPropertiesStore: DocPropertiesStore
  ) {
    super();
  }

  getBlockSuiteDoc(id: string) {
    return (
      this.workspaceService.workspace.docCollection
        .getDoc(id)
        ?.getStore({ id }) ?? null
    );
  }

  getBlocksuiteCollection() {
    return this.workspaceService.workspace.docCollection;
  }

  /**
   * Adds a page-list entry for a new doc. `created` is false when nothing was
   * added, e.g. because the id is already listed.
   */
  createDoc(docId?: string): { id: string; created: boolean } {
    const id = docId ?? nanoid();
    let created = false;

    transact(
      this.workspaceService.workspace.rootYDoc,
      () => {
        const docs = this.workspaceService.workspace.rootYDoc
          .getMap('meta')
          .get('pages');

        if (!docs || !(docs instanceof YArray)) {
          return;
        }

        // An import or template copy can create a doc that is already listed;
        // a second entry for the same id could never be trashed or removed.
        if (groupDocMetaEntries(docs).has(id)) {
          return;
        }

        docs.push([
          new YMap([
            ['id', id],
            ['title', ''],
            ['createDate', Date.now()],
            ['tags', new YArray()],
          ]),
        ]);
        created = true;
      },
      { force: true }
    );

    return { id, created };
  }

  watchDocIds() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(yjsObserve),
      map(meta => {
        if (meta instanceof YArray) {
          return [...groupDocMetaEntries(meta).keys()];
        } else {
          return [];
        }
      })
    );
  }

  watchAllDocUpdatedDate() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.updatedDate')),
      map(pages => {
        if (pages instanceof YArray) {
          return firstDocMetaEntries(pages).map(([id, v]) => ({
            id,
            updatedDate: v.get('updatedDate') as number | undefined,
          }));
        } else {
          return [];
        }
      })
    );
  }

  watchAllDocTagIds() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.tags')),
      map(pages => {
        if (pages instanceof YArray) {
          return firstDocMetaEntries(pages).map(([id, v]) => ({
            id,
            tags: (() => {
              const tags = v.get('tags');
              if (tags instanceof YArray) {
                return tags.toJSON() as string[];
              }
              return (tags ?? []) as string[];
            })(),
          }));
        } else {
          return [];
        }
      })
    );
  }

  watchAllDocCreateDate() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.createDate')),
      map(pages => {
        if (pages instanceof YArray) {
          return firstDocMetaEntries(pages).map(([id, v]) => ({
            id,
            createDate: (v.get('createDate') ?? 0) as number,
          }));
        } else {
          return [];
        }
      })
    );
  }

  watchAllDocTitle() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.title')),
      map(pages => {
        if (pages instanceof YArray) {
          return firstDocMetaEntries(pages).map(([id, v]) => ({
            id,
            title: (v.get('title') ?? '') as string,
          }));
        } else {
          return [];
        }
      })
    );
  }

  watchNonTrashDocIds() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.trash')),
      map(meta => {
        if (meta instanceof YArray) {
          return [...groupDocMetaEntries(meta)]
            .filter(([id, entries]) => id && !entries.some(isTrashedEntry))
            .map(([id]) => id);
        } else {
          return [];
        }
      })
    );
  }

  watchTrashDocIds() {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.trash')),
      map(meta => {
        if (meta instanceof YArray) {
          return [...groupDocMetaEntries(meta)]
            .filter(([id, entries]) => id && entries.some(isTrashedEntry))
            .map(([id]) => id);
        } else {
          return [];
        }
      })
    );
  }

  /**
   * Page-list entries grouped by doc id, computed once per page-list change and
   * shared by every doc record watching its own meta.
   */
  private readonly docMetaEntries$ = yjsGetPath(
    this.workspaceService.workspace.rootYDoc.getMap('meta'),
    'pages'
  ).pipe(
    switchMap(yjsObserve),
    map(pages =>
      pages instanceof YArray
        ? groupDocMetaEntries(pages)
        : new Map<string, DocMetaEntry[]>()
    ),
    share({ connector: () => new ReplaySubject(1) })
  );

  watchDocMeta(id: string) {
    return this.docMetaEntries$.pipe(
      map(groups => groups.get(id) ?? []),
      distinctUntilChanged(
        (prev, curr) =>
          prev.length === curr.length &&
          prev.every((entry, index) => entry === curr[index])
      ),
      switchMap(entries =>
        entries.length === 0
          ? of(entries)
          : combineLatest(entries.map(entry => yjsObserveDeep(entry))).pipe(
              map(() => entries)
            )
      ),
      map(mergeDocMetaEntries)
    );
  }

  watchDocListReady() {
    return this.workspaceService.workspace.engine.doc
      .docState$(this.workspaceService.workspace.id)
      .pipe(map(state => state.synced));
  }

  setDocMeta(id: string, meta: Partial<DocMeta>) {
    this.workspaceService.workspace.docCollection.meta.setDocMeta(id, meta);
  }

  setDocPrimaryModeSetting(id: string, mode: DocMode) {
    return this.docPropertiesStore.updateDocProperties(id, {
      primaryMode: mode,
    });
  }

  getDocPrimaryModeSetting(id: string) {
    return this.docPropertiesStore.getDocProperties(id)?.primaryMode;
  }

  watchDocPrimaryModeSetting(id: string) {
    return this.docPropertiesStore.watchDocProperties(id).pipe(
      map(config => config?.primaryMode),
      distinctUntilChanged((p, c) => p === c)
    );
  }

  waitForDocLoadReady(id: string) {
    return this.workspaceService.workspace.engine.doc.waitForDocLoaded(id);
  }

  addPriorityLoad(id: string, priority: number) {
    return this.workspaceService.workspace.engine.doc.addPriority(id, priority);
  }
}
